import { z } from 'zod';
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { State } from '#~/depot/types.js';

// Stable sessionStorage entry name; preserve it for existing saved notices.
const SESSION_STORAGE_NAME = 'iris.notifications.recording-saved.v1';
let generation = 0;

const recording = z.object({
  roomId: z
    .string()
    .min(1)
    .transform((id) => id as Room['id']),
  deviceId: z
    .string()
    .min(1)
    .transform((id) => id as Device['id']),
  name: z.string(),
  agentId: z.string().min(1),
  bridgeName: z.string(),
  local: z.boolean(),
  confirmed: z.boolean(),
  subsequentAttemptStarted: z.boolean()
});

const batch = z
  .object({
    roomId: z
      .string()
      .min(1)
      .transform((id) => id as Room['id']),
    startedAt: z.number().finite(),
    recordings: z.array(recording),
    closed: z.boolean()
  })
  .refine((value) => value.recordings.every((item) => item.roomId === value.roomId));

const snapshot = z.object({
  owner: z.string().min(1),
  batches: z.array(batch),
  history: z.array(
    z.object({
      id: z.string().startsWith('alert:n6:'),
      title: z.string(),
      message: z.string(),
      timestamp: z.number().finite(),
      isRead: z.boolean()
    })
  )
});

/** A recording result still owned by the notification panel. */
export type StoppedRecording = z.infer<typeof recording>;
/** Closed batches retain unresolved stops for a separate late result. */
export type RecordingBatch = z.infer<typeof batch>;

/** Sign-out retires old writers as well as removing their reload snapshot. */
export const clearRecordingSavedSession = (): void => {
  generation += 1;
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_NAME);
  } catch {
    // Blocked browser storage must not prevent sign-out.
  }
};

/**
 * N6's explicit reload exception: retain only this account's results in this
 * tab/app session. Reuse the notification depot for display; never serialize
 * callbacks, credentials or unrelated alerts. Like onboarding/resume.ts, read
 * synchronously before the first render can overwrite the saved state.
 */
export const openRecordingSavedSession = (owner: string) => {
  let saved: z.infer<typeof snapshot> | undefined;
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_NAME);
    if (raw !== null) {
      const parsed = snapshot.safeParse(JSON.parse(raw));
      if (parsed.success && parsed.data.owner === owner) saved = parsed.data;
      else clearRecordingSavedSession();
    }
  } catch {
    // Corrupt or unavailable storage degrades to the live in-memory owner.
  }
  const epoch = generation;
  let lastSaved: string | undefined;
  return {
    snapshot: saved,
    save: (batches: RecordingBatch[], notifications: State['notifications']): void => {
      if (epoch !== generation) return;
      const history = Object.values(notifications).flatMap(([notice, , status]) =>
        typeof notice.id === 'string' &&
        notice.id.startsWith('alert:n6:') &&
        typeof notice.title === 'string' &&
        typeof notice.message === 'string'
          ? [
              {
                id: notice.id,
                title: notice.title,
                message: notice.message,
                timestamp: notice.timestamp.getTime(),
                isRead: status.isRead
              }
            ]
          : []
      );
      try {
        const serialized = JSON.stringify({ owner, batches, history });
        if (serialized === lastSaved) return;
        window.sessionStorage.setItem(SESSION_STORAGE_NAME, serialized);
        lastSaved = serialized;
      } catch {
        // Quota/private-mode failures leave the existing live notices intact.
      }
    }
  };
};
